/**
 * Module-level seam between the chat realtime layer and the notifications
 * bell (PD-14294).
 *
 * The bell badge (`useNotificationsIndicator`) lives deep in the TopBar and
 * owns the authoritative unread count + the in-tray list. The chat
 * realtime handler (`ChatProvider`) wraps the whole app tree but has no
 * direct reference to that hook's state. Rather than thread a React context
 * through every layer between them, the bell registers its imperative
 * refreshers here on mount, and the chat handler calls `notifyIndicator()`
 * when a realtime "notification" nudge lands on the inbox.
 *
 * Deliberately NOT a count store: the nudge only triggers a re-pull of the
 * authoritative count via REST (and the list, if the tray is open). We
 * never increment a count from here — that would double-count against the
 * next poll.
 */

export interface NotificationIndicatorHandle {
	/** Re-pull the authoritative unread count (drives the bell badge). */
	refresh: () => void | Promise<void>;
	/** Re-pull the in-tray list. Only meaningful while the tray is open. */
	fetchList: () => void | Promise<void>;
	/** Whether the tray is currently open (so we only refetch the list then). */
	isPanelOpen: () => boolean;
}

// The desktop and the mobile bell can BOTH be mounted at once, each
// registering its own handle. A single-handle store would only refresh the
// last-registered (often the hidden mobile bell), so the visible desktop bell
// would miss the realtime nudge — hence a Set fanned out to every handle.
const registeredHandles = new Set<NotificationIndicatorHandle>();

/**
 * A bell registers its imperative refreshers on mount; the returned function
 * unregisters that exact handle on unmount.
 */
export function registerIndicator(
	handle: NotificationIndicatorHandle,
): () => void {
	registeredHandles.add(handle);
	return () => {
		registeredHandles.delete(handle);
	};
}

/**
 * Called by the chat realtime handler when a "notification" nudge arrives.
 * Refreshes every mounted bell's authoritative count (and its list if that
 * bell's tray is open). No-op when no bell is mounted (e.g. a surface without
 * the TopBar). Never increments a count here — that would double-count against
 * the next poll.
 */
export function notifyIndicator(): void {
	for (const handle of registeredHandles) {
		void handle.refresh();
		if (handle.isPanelOpen()) {
			void handle.fetchList();
		}
	}
}
